import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import _ from 'lodash';
import { useMsal } from '@azure/msal-react';
import apiBaseUrl from './apiBaseUrl';
import { loginRequest } from './authConfig';
import { searchTokens } from './searchFold';
import './styles.css';

const PUBLICATIONS_PER_PAGE = 25;
const SIMAP_BASE_URL = process.env.REACT_APP_SIMAP_BASE_URL || 'https://www.simap.ch';

// COLUMN ORDER AND LABELS TO USE
const columns = [
  { key: 'publicationDate', label: 'Datum' },
  { key: 'offerDeadline', label: 'Deadline' },
  { key: 'procOffice', label: 'Beschaffungsstelle' },
  { key: 'projectTitle', label: 'Projektbezeichnung inkl. Link' },
  { key: 'owner', label: 'Zuständig' },
  { key: 'goNoGo', label: 'Interested' },
  { key: 'note', label: 'Notizen' },
];

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function PublicationsPage({ canManageAssignments = false }) {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState([]);
  const [owners, setOwners] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'publicationDate', direction: 'desc' });
  const [editingOwners, setEditingOwners] = useState({}); // { rowIndex: email }
  const [editingGoNoGo, setEditingGoNoGo] = useState({}); // { rowIndex: selectedValue }
  const [editingNotes, setEditingNotes] = useState({}); // { rowIndex: textValue }
  const [expandedNotes, setExpandedNotes] = useState({}); // { rowIndex: boolean }
  const [searchText, setSearchText] = useState(''); // Search filter text
  const [hideNein, setHideNein] = useState(false); // Toggle to hide projects marked as "Nein"
  const [hideExpired, setHideExpired] = useState(true); // Toggle to hide publications past their deadline
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [showAllPublications, setShowAllPublications] = useState(false); // Toggle to show all vs 25
  const [applyStatus, setApplyStatus] = useState({}); // { rowIndex: "success" | "error" | null }
  const [applyGoNoGoStatus, setApplyGoNoGoStatus] = useState({}); // { rowIndex: "success" | "error" | null }
  const [applyNoteStatus, setApplyNoteStatus] = useState({}); // { rowIndex: "success" | "error" | null }
  const loggedInUserEmail = normalizeEmail(accounts[0]?.username || '');
  const initialLoadDone = useRef(false);

  const getAuthHeaders = useCallback(async () => {
    const account = accounts[0] || instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) {
      throw new Error('No authenticated user available.');
    }

    const tokenResponse = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
    });

    return {
      Authorization: `Bearer ${tokenResponse.idToken}`,
    };
  }, [instance, accounts]);

  // Define column keys for logic (needed for useCallback dependency)
  const ownerColKey = 'owner';
  
  // Check if owner is valid (not empty and not "--None--") - only check applied/saved values
  const isOwnerValid = useCallback((row) => {
    const currentOwner = row[ownerColKey];
    return currentOwner && currentOwner !== "" && currentOwner !== "--None--";
  }, [ownerColKey]);

  // Format date to yyyy-mm-dd, hh:mm format
  const formatDeadline = useCallback((dateString) => {
    if (!dateString) return '';
    
    try {
      // First try to parse as a regular Date object
      let date = new Date(dateString);
      
      // If that fails, try to parse dd-mm-yyyy, hh-mm format
      if (isNaN(date.getTime())) {
        // Match dd-mm-yyyy, hh-mm format
        const match = dateString.match(/^(\d{2})-(\d{2})-(\d{4}),?\s*(\d{2})-(\d{2})$/);
        if (match) {
          const [, day, month, year, hours, minutes] = match;
          // Create date from parsed components (month is 0-indexed in Date constructor)
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
        } else {
          // If no match, just convert time dashes to colons and return
          return dateString.replace(/(\d{2})-(\d{2})$/, '$1:$2');
        }
      }
      
      // If we have a valid date, format it as yyyy-mm-dd, hh:mm
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return year + '-' + month + '-' + day + ', ' + hours + ':' + minutes;
      }
      
      return dateString;
    } catch (error) {
      // If all parsing fails, try to convert dashes to colons in time portion
      return dateString.replace(/(\d{2})-(\d{2})$/, '$1:$2');
    }
  }, []);

  // Fetch table data; long-expired publications are only requested when 'Hide expired' is off
  useEffect(() => {
    let cancelled = false;
    const fetchPageData = async () => {
      const initial = !initialLoadDone.current;
      try {
        if (initial) {
          setLoading(true);
        }
        setOwnersLoading(canManageAssignments);
        setFetchError('');
        const headers = await getAuthHeaders();
        const requests = [axios.get(`${apiBaseUrl}/api/publications`, { headers, params: { includeExpired: !hideExpired } })];
        if (canManageAssignments) {
          requests.push(axios.get(`${apiBaseUrl}/api/owners`, { headers }));
        }

        const [pubsResp, ownersResp] = await Promise.all(requests);
        if (cancelled) return;
        if (pubsResp.status === 200) {
          const sortedData = _.orderBy(pubsResp.data, ['publicationDate'], ['desc']);
          setData(sortedData);
          // Row-index keyed UI state is stale once the rows change.
          setSortConfig({ key: 'publicationDate', direction: 'desc' });
          setEditingOwners({});
          setEditingGoNoGo({});
          setEditingNotes({});
          setExpandedNotes({});
          setApplyStatus({});
          setApplyGoNoGoStatus({});
          setApplyNoteStatus({});
        }
        else throw new Error('Failed to fetch publications');

        if (canManageAssignments) {
          const ownerOptions = ownersResp && ownersResp.status === 200
            ? _.sortBy(
                ownersResp.data.map(owner => ({
                  email: normalizeEmail(owner.email),
                  name: String(owner.name || owner.email || '').trim(),
                })),
                owner => owner.name.toLowerCase()
              )
            : [];
          setOwners(ownerOptions);
        } else {
          setOwners([]);
        }
        initialLoadDone.current = true;
      } catch (e) {
        if (cancelled) return;
        setFetchError('Failed to load publications. Please refresh the page or try again later.');
        console.error('Error loading:', e, e?.response?.status, e?.response?.data);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setOwnersLoading(false);
        }
      }
    };
    fetchPageData();
    return () => { cancelled = true; };
  }, [apiBaseUrl, canManageAssignments, getAuthHeaders, hideExpired]);

  // Table sorting by key
  const onSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
    const sortedData = _.orderBy(data, [key], [direction]);
    setData(sortedData);
  };

  if (loading) return <div style={{ minHeight: '70vh' }} />;
  if (fetchError) {
    return (
      <div style={{ padding: '24px', color: '#b00020' }}>
        {fetchError}
      </div>
    );
  }
  if (data.length === 0 && !hideExpired) {
    return (
      <div style={{ padding: '24px' }}>
        No publications available.
      </div>
    );
  }

  // Calculate remaining column keys for logic
  const projectTitleColKey = 'projectTitle';
  const goNoGoColKey = 'goNoGo';
  const noteColKey = 'note';
  const idColKey = 'projectId';
  const offerDeadlineColKey = 'offerDeadline';

  // Used when applying new go/no-go
  const handleGoNoGoChange = (rowIndex, value) => {
    setEditingGoNoGo(prev => ({ ...prev, [rowIndex]: value }));
    setApplyGoNoGoStatus(prev => ({ ...prev, [rowIndex]: null }));
  };

  const handleOwnerChange = (rowIndex, value) => {
    setEditingOwners(prev => ({ ...prev, [rowIndex]: normalizeEmail(value) }));
    setApplyStatus(prev => ({ ...prev, [rowIndex]: null }));
  };

  // Used when editing notes
  const handleNoteChange = (rowIndex, value) => {
    setEditingNotes(prev => ({ ...prev, [rowIndex]: value }));
    setApplyNoteStatus(prev => ({ ...prev, [rowIndex]: null }));
  };

  // Toggle expanded view for notes
  const toggleNoteExpansion = (rowIndex) => {
    setExpandedNotes(prev => ({ ...prev, [rowIndex]: !prev[rowIndex] }));
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  // Format email into display name, e.g. ab.dc@123.com -> AB DC
  const formatOwnerDisplayName = (ownerValue) => {
    if (!ownerValue || typeof ownerValue !== 'string') return '';

    const trimmed = ownerValue.trim();
    if (!trimmed.includes('@')) return trimmed;

    const localPart = trimmed.split('@')[0];
    const nameParts = localPart
      .split(/[._-]+/)
      .map(part => part.trim())
      .filter(Boolean);

    if (nameParts.length === 0) return trimmed;
    return nameParts.map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ');
  };

  const getOwnerName = (ownerEmail) => {
    const normalizedOwnerEmail = normalizeEmail(ownerEmail);
    const matchingOwner = owners.find(owner => owner.email === normalizedOwnerEmail);
    return matchingOwner ? matchingOwner.name : formatOwnerDisplayName(ownerEmail);
  };

  // Filter data based on search text and "Nein" filter
  const filteredData = data.filter(row => {
    // Filter out "Nein" if hideNein toggle is enabled
    if (hideNein && row[goNoGoColKey] === 'Nein') {
      return false;
    }

    // Filter out expired publications if hideExpired toggle is enabled
    if (hideExpired) {
      const rawDeadline = row[offerDeadlineColKey];
      if (rawDeadline && String(rawDeadline).trim().toUpperCase() !== 'NA') {
        let deadlineDate = new Date(rawDeadline);
        if (isNaN(deadlineDate.getTime())) {
          const match = String(rawDeadline).match(/^(\d{2})-(\d{2})-(\d{4}),?\s*(\d{2})-(\d{2})$/);
          if (match) {
            const [, day, month, year, hours, minutes] = match;
            deadlineDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
          }
        }
        if (!isNaN(deadlineDate.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (deadlineDate < today) return false;
        }
      }
    }
    
    if (!searchText.trim()) return true; // Show all if no search text
    
    // Same folding and tokenizing as the search index built by the import job
    const searchTerms = searchTokens(searchText);
    
    if (searchTerms.length === 0) return true;
    
    // Check if ALL search terms occur in the row's search index (AND logic)
    const rowSearchText = row.searchText;
    if (!rowSearchText) return false;
    return searchTerms.every(searchTerm => rowSearchText.includes(searchTerm));
  });

  // Limit displayed data to 25 unless "show all" is clicked
  const displayedData = showAllPublications ? filteredData : filteredData.slice(0, PUBLICATIONS_PER_PAGE);

  // "PUT" the update for the given row
  const handleApplyOwner = async (row, rowIndex) => {
    const selectedOwner = editingOwners.hasOwnProperty(rowIndex)
      ? editingOwners[rowIndex]
      : normalizeEmail(row[ownerColKey]);
    const newOwner = canManageAssignments ? selectedOwner : loggedInUserEmail;
    setApplyStatus(prev => ({ ...prev, [rowIndex]: 'saving' }));

    if (!newOwner) {
      setApplyStatus(prev => ({ ...prev, [rowIndex]: 'error' }));
      return;
    }

    try {
      const payload = {
        projectId: row[idColKey],
        [ownerColKey]: newOwner,
      };

      const headers = await getAuthHeaders();
      await axios.put(`${apiBaseUrl}/api/publications`, payload, { headers });
      setData(
        data.map((r, idx) => idx === rowIndex ? { 
          ...r, 
          [ownerColKey]: newOwner,
        } : r)
      );
      setEditingOwners(prev => ({ ...prev, [rowIndex]: newOwner }));

      setApplyStatus(prev => ({ ...prev, [rowIndex]: 'success' }));
    } catch (err) {
      setApplyStatus(prev => ({ ...prev, [rowIndex]: 'error' }));
      const backendErrorMessage = err?.response?.data?.error || err?.message || 'Failed to update owner.';
      window.alert(backendErrorMessage);
      console.error('Failed to update owner:', err);
    }
  };

  // "PUT" the update for Go/NoGo
  const handleApplyGoNoGo = async (row, rowIndex) => {
    const newGoNoGo = editingGoNoGo[rowIndex];
    setApplyGoNoGoStatus(prev => ({ ...prev, [rowIndex]: 'saving' }));

    try {
      const payload = {
        projectId: row[idColKey],
        [goNoGoColKey]: newGoNoGo,
      };
      const headers = await getAuthHeaders();
      await axios.put(`${apiBaseUrl}/api/publications`, payload, { headers });
      setData(
        data.map((r, idx) => idx === rowIndex ? { ...r, [goNoGoColKey]: newGoNoGo } : r)
      );
      setApplyGoNoGoStatus(prev => ({ ...prev, [rowIndex]: 'success' }));
    } catch (err) {
      setApplyGoNoGoStatus(prev => ({ ...prev, [rowIndex]: 'error' }));
      console.error('Failed to update go/no-go:', err);
    }
  };

  // "PUT" the update for Notes
  const handleApplyNote = async (row, rowIndex) => {
    const newNote = editingNotes[rowIndex];
    setApplyNoteStatus(prev => ({ ...prev, [rowIndex]: 'saving' }));

    try {
      const payload = {
        projectId: row[idColKey],
        [noteColKey]: newNote,
      };
      const headers = await getAuthHeaders();
      await axios.put(`${apiBaseUrl}/api/publications`, payload, { headers });
      setData(
        data.map((r, idx) => idx === rowIndex ? { ...r, [noteColKey]: newNote } : r)
      );
      setApplyNoteStatus(prev => ({ ...prev, [rowIndex]: 'success' }));
      // Exit editing mode after successful save
      setEditingNotes(prev => {
        const newState = { ...prev };
        delete newState[rowIndex];
        return newState;
      });
    } catch (err) {
      setApplyNoteStatus(prev => ({ ...prev, [rowIndex]: 'error' }));
      console.error('Failed to update note:', err);
    }
  };

  return (
    <div className="publications-container">
      <h1>Current Projects</h1>
      
      {/* Search Box + Filter on same row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px',
        marginBottom: '20px',
        maxWidth: '80%',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}>
        <label style={{ fontSize: '14px', fontWeight: 'bold' }}>
          Search:
        </label>
        <input
          type="text"
          placeholder="Search keywords (separate with spaces or commas)"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            width: '450px',
            outline: 'none'
          }}
        />
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            style={{
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f5f5f5',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>
        )}
        {(searchText || hideNein || hideExpired) && (
          <span style={{ fontSize: '14px', color: '#666' }}>
            {filteredData.length} of {data.length} projects shown
          </span>
        )}

        {/* Spacer + "Hide Nein" checkbox */}
        <div style={{ marginLeft: '30px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            id="hideNeinCheckbox"
            checked={hideNein}
            onChange={e => setHideNein(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <label htmlFor="hideNeinCheckbox" style={{ cursor: 'pointer', fontSize: '14px', margin: 0 }}>
            Hide not interested (Nein)
          </label>
        </div>

        {/* "Hide Expired" checkbox */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            id="hideExpiredCheckbox"
            checked={hideExpired}
            onChange={e => setHideExpired(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <label htmlFor="hideExpiredCheckbox" style={{ cursor: 'pointer', fontSize: '14px', margin: 0 }}>
            Hide expired publications
          </label>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            {columns.map(({ key, label }) => (
              <th 
                key={key} 
                onClick={key !== noteColKey ? () => onSort(key) : undefined}
                style={{ cursor: key !== noteColKey ? 'pointer' : 'default' }}
              >
                {label} {sortConfig.key === key ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayedData.map((row, rowIndex) => {
            // Find the original index in the full data array for state management
            const originalIndex = data.findIndex(r => r[idColKey] === row[idColKey]);
            const selectedOwner = editingOwners.hasOwnProperty(originalIndex)
              ? editingOwners[originalIndex]
              : normalizeEmail(row[ownerColKey]);
            const hasMatchingOwnerOption = owners.some(owner => owner.email === selectedOwner);
            const isAssigned = isOwnerValid(row);
            const canEditGoNoGo = canManageAssignments ? isAssigned : normalizeEmail(row[ownerColKey]) === loggedInUserEmail;
            
            return (
            <tr key={row[idColKey] || rowIndex}>
              {columns.map(({ key }, colIndex) => (
                <td key={colIndex}>
                  {key === projectTitleColKey ? (
                    // External link on Project Title using ID
                    <a
                      href={`${SIMAP_BASE_URL}/en/project-detail/${encodeURIComponent(row[idColKey])}#invitationtotender`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row[projectTitleColKey]}
                    </a>
                  ) : key === ownerColKey ? (
                    // Owner assignment: show button if empty, otherwise show assigned owner
                    <div className="owner-controls">
                      {canManageAssignments ? (
                        <>
                          <select
                            value={selectedOwner}
                            onChange={e => handleOwnerChange(originalIndex, e.target.value)}
                            className={`form-select ${ownersLoading || owners.length === 0 ? 'disabled-select' : ''}`}
                            disabled={ownersLoading || owners.length === 0}
                            title={ownersLoading ? 'Loading users...' : ''}
                          >
                            <option value="">Select user</option>
                            {!hasMatchingOwnerOption && selectedOwner && (
                              <option value={selectedOwner}>{formatOwnerDisplayName(row[ownerColKey])}</option>
                            )}
                            {owners.map(owner => (
                              <option key={owner.email} value={owner.email}>
                                {owner.name}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleApplyOwner(row, originalIndex)}
                            disabled={
                              !selectedOwner ||
                              selectedOwner === normalizeEmail(row[ownerColKey]) ||
                              ownersLoading ||
                              (applyStatus[originalIndex] === 'saving')
                            }
                            className={`table-button ${
                              !selectedOwner ||
                              selectedOwner === normalizeEmail(row[ownerColKey]) ||
                              ownersLoading ||
                              (applyStatus[originalIndex] === 'saving') ? 'disabled' : ''
                            }`}
                          >
                            Übernehmen
                          </button>
                        </>
                      ) : (
                        isOwnerValid(row) ? (
                          <span>{getOwnerName(row[ownerColKey])}</span>
                        ) : (
                          <button
                            onClick={() => handleApplyOwner(row, originalIndex)}
                            disabled={
                              !loggedInUserEmail ||
                              (applyStatus[originalIndex] === 'saving')
                            }
                            className={`table-button ${
                              !loggedInUserEmail ||
                              (applyStatus[originalIndex] === 'saving') ? 'disabled' : ''
                            }`}
                          >
                            Übernehmen
                          </button>
                        )
                      )}
                      {applyStatus[originalIndex] === 'success' && (
                        <span className="status-success">✓</span>
                      )}
                      {applyStatus[originalIndex] === 'error' && (
                        <span className="status-error">✗</span>
                      )}
                    </div>
                  ) : key === goNoGoColKey ? (
                    // Interested editable select (disabled when no owner assigned)
                    <div className="owner-controls">
                      <select
                        value={
                          editingGoNoGo.hasOwnProperty(originalIndex)
                            ? editingGoNoGo[originalIndex]
                            : row[goNoGoColKey] || ""
                        }
                        onChange={e => handleGoNoGoChange(originalIndex, e.target.value)}
                        className={`form-select ${!canEditGoNoGo ? 'disabled-select' : ''}`}
                        disabled={!canEditGoNoGo}
                        title={!canEditGoNoGo ? (canManageAssignments ? 'Assign a user before changing this value' : 'Only the assigned person can change this value') : ''}
                      >
                        <option value="">--None--</option>
                        <option value="Ja">Ja</option>
                        <option value="Nein">Nein</option>
                      </select>
                      <button
                        onClick={() => handleApplyGoNoGo(row, originalIndex)}
                        disabled={
                          !canEditGoNoGo ||
                          (editingGoNoGo[originalIndex] === row[goNoGoColKey]) ||
                          (applyGoNoGoStatus[originalIndex] === 'saving')
                        }
                        className={`table-button ${
                          !canEditGoNoGo ||
                          (editingGoNoGo[originalIndex] === row[goNoGoColKey]) ||
                          (applyGoNoGoStatus[originalIndex] === 'saving') ? 'disabled' : ''
                        }`}
                      >
                        Apply
                      </button>
                      {applyGoNoGoStatus[originalIndex] === 'success' && (
                        <span className="status-success">✓</span>
                      )}
                      {applyGoNoGoStatus[originalIndex] === 'error' && (
                        <span className="status-error">✗</span>
                      )}
                    </div>
                  ) : key === noteColKey ? (
                    // Notes editable text input
                    <div className="owner-controls">
                      {editingNotes.hasOwnProperty(originalIndex) ? (
                        // Editing mode - show textarea
                        <>
                          <textarea
                            value={editingNotes[originalIndex]}
                            onChange={e => handleNoteChange(originalIndex, e.target.value)}
                            className="form-select"
                            placeholder="Enter note..."
                            rows="3"
                            style={{ width: '300px', resize: 'vertical' }}
                          />
                          <div>
                            <button
                              onClick={() => handleApplyNote(row, originalIndex)}
                              disabled={
                                (editingNotes[originalIndex] === row[noteColKey]) ||
                                (applyNoteStatus[originalIndex] === 'saving')
                              }
                              className={`table-button ${
                                (editingNotes[originalIndex] === row[noteColKey]) ||
                                (applyNoteStatus[originalIndex] === 'saving') ? 'disabled' : ''
                              }`}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingNotes(prev => {
                                  const newState = { ...prev };
                                  delete newState[originalIndex];
                                  return newState;
                                });
                                setApplyNoteStatus(prev => ({ ...prev, [originalIndex]: null }));
                              }}
                              className="table-button"
                              style={{ marginLeft: '5px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        // Display mode - show truncated text with expand option
                        <div style={{ width: '300px' }}>
                          <div style={{ 
                            marginBottom: '5px', 
                            minHeight: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}>
                            <span style={{ 
                              whiteSpace: expandedNotes[originalIndex] ? 'pre-wrap' : 'nowrap',
                              flex: '1',
                              overflow: expandedNotes[originalIndex] ? 'visible' : 'hidden'
                            }}>
                              {expandedNotes[originalIndex] ? 
                                (row[noteColKey] || "") : 
                                truncateText(row[noteColKey])
                              }
                            </span>
                            <button
                              onClick={() => {
                                setEditingNotes(prev => ({ 
                                  ...prev, 
                                  [originalIndex]: row[noteColKey] || "" 
                                }));
                              }}
                              className="table-button"
                              style={{ flexShrink: 0 }}
                            >
                              Edit
                            </button>
                          </div>
                          <div>
                            {row[noteColKey] && row[noteColKey].length > 100 && (
                              <button
                                onClick={() => toggleNoteExpansion(originalIndex)}
                                className="table-button"
                              >
                                {expandedNotes[originalIndex] ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {applyNoteStatus[originalIndex] === 'success' && (
                        <span className="status-success">✓</span>
                      )}
                      {applyNoteStatus[originalIndex] === 'error' && (
                        <span className="status-error">✗</span>
                      )}
                    </div>
                  ) : key === offerDeadlineColKey ? (
                    // Format deadline in dd-mm-yy, hh:mm format
                    formatDeadline(row[key])
                  ) : (
                    row[key]
                  )}
                </td>
              ))}
            </tr>
            );
          })}
        </tbody>
      </table>

      {/* Show all publications button */}
      {filteredData.length > PUBLICATIONS_PER_PAGE && !showAllPublications && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={() => setShowAllPublications(true)}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '4px',
              background: '#007bff',
              color: 'white',
              cursor: 'pointer',
              transition: 'background 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = '#0056b3'}
            onMouseLeave={(e) => e.target.style.background = '#007bff'}
          >
            Show all publications ({filteredData.length} total)
          </button>
        </div>
      )}
    </div>
  );
}

export default PublicationsPage;
